import React, { useState, useEffect } from 'react';
import { graphql } from 'gatsby';
import { PageProps } from 'gatsby';
import styled from 'styled-components';
import {
  Layout,
  Seo,
  Section,
  Slider,
  Text,
  Flex,
  Columns,
  Box,
  Timerex,
  Form,
} from '../../components/blocks';
import { EventForm } from '../../components/pages/resource/form';
import Resource from '../../@types/resource';

type Props = PageProps & {
  slim?: boolean;
  pageContext: {
    resourceId: string;
  };
  data: {
    microcmsResource: Resource.MicrocmsResourceContent;
  };
};

const ImgFrame = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spaces.l};
  border-top: 1px solid ${({ theme }) => theme.borderColors.default};
  border-bottom: 1px solid ${({ theme }) => theme.borderColors.default};
  background: ${({ theme }) => theme.backgroundColors.default};
  height: 320px;

  ${({ theme }) => theme.screen.sp({ height: '240px' })}

  img {
    width: auto;
    height: 100%;
  }
`;

const BlogContent = styled.div`
  color: ${({ theme }) => theme.fontColors.default};
  width: 100%;

  h2 {
    font-size: ${({ theme }) => theme.fontSizes.xl};
    padding: ${({ theme }) => theme.spaces.l} 0;
    line-height: ${({ theme }) => theme.lineHeights.m};
    word-wrap: break-word;
  }

  h3 {
    font-size: ${({ theme }) => theme.fontSizes.l};
    padding: ${({ theme }) => theme.spaces.m} 0;
    line-height: ${({ theme }) => theme.lineHeights.m};
    word-wrap: break-word;
  }

  h4,
  h5,
  h6 {
    font-size: ${({ theme }) => theme.fontSizes.m};
    padding: ${({ theme }) => theme.spaces.m} 0;
    line-height: ${({ theme }) => theme.lineHeights.m};
    word-wrap: break-word;
  }

  p {
    font-size: ${({ theme }) => theme.fontSizes.s};
    margin-bottom: ${({ theme }) => theme.spaces.l};
    line-height: ${({ theme }) => theme.lineHeights.l};
    word-wrap: break-word;
  }

  ul,
  ol {
    margin-bottom: ${({ theme }) => theme.spaces.xl};
  }

  li {
    font-size: ${({ theme }) => theme.fontSizes.s};
    line-height: ${({ theme }) => theme.lineHeights.l};
  }

  a {
    color: ${({ theme }) => theme.fontColors.default};
    box-shadow: 0 1px 0 0 ${({ theme }) => theme.fontColors.default};
    word-wrap: break-word;
    transition-duration: ${({ theme }) => theme.transitions.normal};

    &:hover {
      color: ${({ theme }) => theme.fontColors.primary};
      box-shadow: 0 1px 0 0 ${({ theme }) => theme.fontColors.primary};
      text-decoration: none;
    }
  }

  img {
    width: 100%;
    height: auto;
  }

  blockquote,
  code {
    padding: ${({ theme }) => theme.spaces.xl};
    background: ${({ theme }) => theme.backgroundColors.default};
    line-height: ${({ theme }) => theme.lineHeights.l};
    font-size: ${({ theme }) => theme.fontSizes.s};
    word-wrap: break-word;
  }

  code {
    word-wrap: break-word;
    padding: ${({ theme }) => `${theme.spaces.m} ${theme.spaces.l}`};
  }

  iframe {
    width: 100%;
    border: 8px solid ${({ theme }) => theme.backgroundColors.default};
    border-radius: ${({ theme }) => theme.radii.l};

    ${({ theme }) =>
      theme.screen.sp({
        width: '100%',
        height: '320px',
      })}
  }
`;

const Contents = styled(Box)`
  padding: ${({ theme }) => theme.spaces.l};
`;

const PreviewItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 320px;
  padding: ${({ theme }) => theme.spaces.l};

  img {
    width: auto;
    max-width: 100%;
    height: auto;
    max-height: 100%;
  }

  ${({ theme }) =>
    theme.screen.sp({
      maxHeight: '240px',
    })}
`;

const OverrideBox = styled(Box)`
  border: 1px solid ${({ theme }) => theme.borderColors.default};
  border-radius: ${({ theme }) => theme.radii.l};
  width: 100%;
  max-width: 614px;

  ${({ theme }) =>
    theme.screen.sp({
      maxWidth: '100%',
    })};

  ${Text} {
    padding: ${({ theme }) => `${theme.spaces.m} ${theme.spaces.l}`};

    ${({ theme }) =>
      theme.screen.sp({
        padding: `${theme.spaces.s} ${theme.spaces.l}`,
        fontSize: theme.fontSizes.m,
      })}
  }
`;

const SingleColumn = styled(Section)`
  max-width: ${({ theme }) => theme.windowSizes.s};
  margin: 0 auto ${({ theme }) => theme.spaces.ex};
  gap: ${({ theme }) => theme.spaces.xxl};
`;

const OverrideColumns = styled(Columns)`
  grid-template-columns: 1fr 360px;
  column-gap: ${({ theme }) => theme.spaces.xxxl};

  ${({ theme }) =>
    theme.screen.sp({
      gridTemplateColumns: '1fr',
      width: '100%',
    })};

  ${Columns.Item} {
    &:first-child {
      width: 100%;
      overflow: hidden;
    }
  }
`;

const Root = styled(Flex)`
  flex-direction: column;
  align-items: center;
  max-width: ${({ theme }) => theme.windowSizes.m};
  padding: ${({ theme }) =>
    `${theme.spaces.l} ${theme.spaces.xxl} ${theme.spaces.xxl}`};
  margin: 0 auto ${({ theme }) => theme.spaces.ex};

  ${({ theme }) =>
    theme.screen.sp({
      paddingTop: 0,
    })}
`;

const parseHashParams = (hash: string) => {
  const params = new URLSearchParams(hash.slice(1));
  const paramsObject: { [key: string]: string } = {};
  params.forEach((value, key) => {
    paramsObject[key] = value;
  });
  return paramsObject;
};

export const query = graphql`
  query ResourceContent($resourceId: String!) {
    microcmsResource(resourceId: { eq: $resourceId }) {
      id
      publishedAt(formatString: "YYYY-MM-DD HH:MM")
      resourceId
      content
      key_visual {
        url
        height
        width
      }
      title
      pardot_url
      pardot_param
      meta {
        title
        description
        keyword
        og_image {
          url
        }
      }
      mixed {
        fieldId
        htmlTag
        richEditor
      }
      preview {
        image {
          url
        }
      }
      timerex {
        publication
        embed
      }
      layout {
        notlist
        column
        simple
      }
    }
  }
`;

export const ResourceContent: React.FC<Props> = ({
  data = { microcmsResource: null },
  location,
}) => {
  const { microcmsResource } = data || {};

  if (!microcmsResource) {
    return null; // または適切なエラーメッセージを表示
  }

  const [utmSource, setUtmSource] = useState<string>('');
  const [utmMedium, setUtmMedium] = useState<string>('');
  const [utmCampaign, setUtmCampaign] = useState<string>('');

  useEffect(() => {
    // 画面プレビュー時にlocation.hashがあると表示されないので、if文追加
    if (location?.hash) {
      // 初期ロード時にセッションストレージからパラメータを読み込む
      const storedUtmSource = sessionStorage.getItem('utm_source');
      const storedUtmMedium = sessionStorage.getItem('utm_medium');
      const storedUtmCampaign = sessionStorage.getItem('utm_campaign');

      if (storedUtmSource) setUtmSource(storedUtmSource);
      if (storedUtmMedium) setUtmMedium(storedUtmMedium);
      if (storedUtmCampaign) setUtmCampaign(storedUtmCampaign);

      const params = parseHashParams(location.hash);
      if (params['utm_source']) {
        setUtmSource(params['utm_source']);
        sessionStorage.setItem('utm_source', params['utm_source']);
      }
      if (params['utm_medium']) {
        setUtmMedium(params['utm_medium']);
        sessionStorage.setItem('utm_medium', params['utm_medium']);
      }
      if (params['utm_campaign']) {
        setUtmCampaign(params['utm_campaign']);
        sessionStorage.setItem('utm_campaign', params['utm_campaign']);
      }
    }
  }, [location?.hash]);

  const formURL = microcmsResource.pardot_url;
  const pardotParam = microcmsResource.pardot_param;

  if (!microcmsResource.layout) {
    return null;
  }

  return (
    <Layout slim={microcmsResource.layout.simple === true}>
      {/* 1カラム */}
      {microcmsResource.layout.column === true ? (
        <SingleColumn>
          {microcmsResource.mixed?.map((mixed, i) => (
            <BlogContent
              key={i}
              dangerouslySetInnerHTML={{
                __html:
                  mixed.fieldId === 'html'
                    ? mixed.htmlTag || ''
                    : mixed.fieldId === 'content'
                    ? mixed.richEditor || ''
                    : microcmsResource.content || '',
              }}
            />
          ))}
          {microcmsResource.content && (
            <BlogContent
              dangerouslySetInnerHTML={{ __html: microcmsResource.content }}
            />
          )}

          {microcmsResource.timerex?.publication && (
            <Timerex embed={microcmsResource.timerex?.embed} />
          )}
          {microcmsResource.pardot_url && <EventForm to={formURL} />}
        </SingleColumn>
      ) : microcmsResource.layout.column === false ? (
        // 2カラム
        <Root>
          <OverrideColumns column="two" alignItems="flex-start">
            <Columns.Item>
              <OverrideBox>
                <Text as="h1" lineHeight="m" size="l" fontWeight="bold">
                  {microcmsResource.title}
                </Text>

                {microcmsResource.preview.length === 0 ? (
                  <ImgFrame>
                    <img src={microcmsResource.key_visual.url} />
                  </ImgFrame>
                ) : (
                  <Slider>
                    {microcmsResource.preview.map((preview, i) => (
                      <PreviewItem
                        className={`keen-slider__slide number-slide${i + 1}`}
                        key={i}
                      >
                        <img
                          src={preview.image.url}
                          height="auto"
                          width="100%"
                        />
                      </PreviewItem>
                    ))}
                  </Slider>
                )}

                <Contents>
                  <BlogContent
                    dangerouslySetInnerHTML={{
                      __html: microcmsResource.content,
                    }}
                  />

                  {microcmsResource.timerex?.publication && (
                    <Timerex embed={microcmsResource.timerex?.embed} />
                  )}
                </Contents>
              </OverrideBox>
            </Columns.Item>
            <Columns.Item>
              <Form
                url={formURL}
                utmSource={utmSource}
                utmMedium={utmMedium}
                utmCampaign={utmCampaign}
                size="s"
                id="inquiry"
                companyName={pardotParam.includes('医療機関名')}
                companyPosition={pardotParam.includes('役職')}
                opening={pardotParam.includes('開業分院状況') && true}
                visit={pardotParam.includes('訪問歯科実施状況')}
                inquiry={pardotParam.includes('問い合わせ内容')}
                receiptComputer={pardotParam.includes(
                  'お使いのレセコンメーカー',
                )}
                download
              />
              {/* <EventForm to={data.pardot_url} /> */}
            </Columns.Item>
          </OverrideColumns>
        </Root>
      ) : null}
    </Layout>
  );
};

export default ResourceContent;

export const Head = ({ data, location }) => {
  const { microcmsResource } = data;

  return (
    <Seo
      title={microcmsResource.meta.title}
      description={microcmsResource.meta.description}
      keywords={microcmsResource.meta.keyword}
      ogImage={microcmsResource.meta.og_image.url}
      ogUrl={`https://dentis-cloud.com/resource/${microcmsResource.resourceId}`}
      canonical={location.pathname}
    />
  );
};
